/**
 * `summarize` — the one sentence the agent says out loud.
 *
 * On the MCP Apps surface the widget is optional chrome; on a voice client it
 * does not exist at all. For most users this string IS the product. So it is
 * built to do four things, in this order, because that is the order a person
 * needs them:
 *
 *   1. the COUNT, first, because that is the answer
 *   2. the CONSTRAINT that was applied, so the user can hear whether we
 *      understood them ("after 20:00", "tonight", "in Zagreb")
 *   3. the STANDOUT option, so a follow-up question is not required
 *   4. the STALENESS, whenever the data is not live
 *
 * It never invents a fact that is not in the rows, and it never omits staleness.
 */
import type { Capability, View } from '@clawharbor/manifest';
import type { SummarizeOpts, Row } from './types.js';
import { normalizeResult } from './result.js';
import { resolveFreshness } from './chrome.js';
import { badgesFor, groupRows, lastSegment, resolveSlot, slotText, stringify } from './slots.js';

/** Inputs that describe pagination or plumbing, not the user's intent. */
const NOISE = /^(limit|offset|page|cursor|sort|order|locale|lang|format_version|timezone|tz|debug)$/i;
const AFTER = /^(after|from|earliest|since|start|start_?time|min_?time|not_?before)$/i;
const BEFORE = /^(before|until|latest|end|end_?time|max_?time|not_?after)$/i;
const DATEKEY = /(^|_)(date|day|when|on)$/i;
const CITYKEY = /(city|town|region|area)/i;
const PLACEKEY = /(cinema|venue|store|location|branch|theatre|theater|shop|centre|center)/i;
const QUERYKEY = /^(q|query|search|term|title|name|film|movie|product)$/i;
const TIMEISH = /^\d{1,2}:\d{2}/;

const VERB = /^(find|list|get|search|show|browse|lookup|fetch|query|check|discover)[_-]/;

/** `find_showtimes` → `showtimes`. The tool name is the best noun we have. */
export function inferNoun(cap: Pick<Capability, 'name' | 'id'>): string {
  const base = (cap.name || cap.id || '').replace(VERB, '').replace(/[_-]+/g, ' ').trim();
  return base || 'results';
}

export function pluralize(noun: string, count: number): string {
  if (count === 1) {
    if (/(ss|us|is)$/i.test(noun)) return noun;
    return noun.replace(/ies$/i, 'y').replace(/s$/i, '');
  }
  if (/s$/i.test(noun)) return noun;
  if (/y$/i.test(noun) && !/[aeiou]y$/i.test(noun)) return noun.replace(/y$/i, 'ies');
  return `${noun}s`;
}

function lower(s: string): string {
  return s.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_\-.]+/g, ' ').trim().toLowerCase();
}

function valueText(v: unknown, labels?: Record<string, string>): string {
  if (Array.isArray(v)) {
    const parts = v.map((x) => valueText(x, labels)).filter(Boolean);
    if (parts.length <= 1) return parts[0] ?? '';
    return `${parts.slice(0, -1).join(', ')} or ${parts[parts.length - 1]}`;
  }
  return stringify(v, labels);
}

/**
 * "tonight" beats "on 2026-08-29" every time, and "tonight" specifically is
 * only honest when the constraint really is an evening one — hence `evening`.
 */
export function relativeDay(value: unknown, now: Date, evening = false): string | undefined {
  const s = stringify(value);
  if (!s) return undefined;
  const iso = /^\d{8}$/.test(s) ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6)}` : s;

  // Date-only strings are parsed as UTC midnight by `new Date`, which lands on the
  // previous local day west of Greenwich. Build them in local time instead.
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  const d = ymd ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])) : new Date(iso);
  if (Number.isNaN(d.getTime())) return /^(today|tonight|tomorrow)$/i.test(s) ? s.toLowerCase() : `on ${s}`;

  const dayMs = 86_400_000;
  const startOf = (x: Date) => Date.UTC(x.getFullYear(), x.getMonth(), x.getDate());
  const delta = Math.round((startOf(d) - startOf(now)) / dayMs);

  if (delta === 0) return evening ? 'tonight' : 'today';
  if (delta === 1) return 'tomorrow';
  if (delta === -1) return 'yesterday';
  if (delta > 1 && delta < 7) return `on ${d.toLocaleDateString('en-GB', { weekday: 'long' })}`;
  return `on ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
}

interface Clauses {
  adjectives: string[];
  time?: string;
  /** True when `time` is an evening constraint, which licenses "tonight". */
  evening: boolean;
  dateValue?: unknown;
  place?: string;
  city?: string;
  match?: string;
  others: string[];
}

/**
 * Turn the applied input into English fragments. Anything unrecognised still
 * gets said (as "key value") rather than dropped — silently ignoring a filter
 * the user asked for is how a summary becomes a lie.
 */
export function describeConstraints(applied: Record<string, unknown>, labels?: Record<string, string>): Clauses {
  const c: Clauses = { adjectives: [], evening: false, others: [] };

  for (const [key, raw] of Object.entries(applied ?? {})) {
    if (raw == null || raw === '' || raw === false || NOISE.test(key)) continue;
    if (Array.isArray(raw) && raw.length === 0) continue;

    if (raw === true) {
      c.adjectives.push(lower(key));
      continue;
    }

    const text = valueText(raw, labels);
    if (!text) continue;

    if (AFTER.test(key) && TIMEISH.test(text)) {
      c.time = `after ${text}`;
      c.evening = Number(text.split(':')[0]) >= 17;
      continue;
    }
    if (BEFORE.test(key) && TIMEISH.test(text)) {
      c.time = `before ${text}`;
      continue;
    }
    if (DATEKEY.test(key)) {
      c.dateValue = raw;
      continue;
    }
    if (CITYKEY.test(key)) {
      c.city = text;
      continue;
    }
    if (PLACEKEY.test(key)) {
      c.place = `at ${text}`;
      continue;
    }
    if (QUERYKEY.test(key)) {
      c.match = `matching "${text}"`;
      continue;
    }
    // A short bare token reads as an adjective ("imax", "subtitled");
    // anything longer needs its key to stay intelligible.
    if (!text.includes(' ') && text.length <= 16) c.adjectives.push(text.toLowerCase());
    else c.others.push(`${lower(key)} ${text}`);
  }
  return c;
}

/** "earliest 20:15 at Arena (IMAX)" — the follow-up question, pre-answered. */
function standout(view: View | undefined, row: Row | undefined, labels?: Record<string, string>): string | undefined {
  if (!row) return undefined;
  const slots = view?.slots ?? {};
  const timeText = slotText(slots['time'], row, labels);
  const primary = timeText ?? slotText(slots['title'], row, labels);
  if (!primary) return undefined;

  const qualifier = slotText(slots['venue'], row, labels) ?? slotText(slots['subtitle'], row, labels);
  const chips = badgesFor(view?.badges ?? [], row, labels)
    .filter((b) => b.tone !== 'danger')
    .slice(0, 2)
    .map((b) => b.label);

  // "earliest" is only true when the primary value is a clock time; otherwise the
  // row order is the binding's sort, which we can only honestly call "first".
  const lead = timeText && TIMEISH.test(timeText) ? 'earliest' : 'first';
  const tail = chips.length ? ` (${chips.join(', ')})` : '';
  return `${lead} ${primary}${qualifier ? ` at ${qualifier}` : ''}${tail}`;
}

function join(parts: Array<string | undefined>): string {
  return parts.filter((p): p is string => Boolean(p && p.trim())).join(' ').replace(/\s+/g, ' ').trim();
}

/**
 * Build the sentence.
 *
 * @param cap    the capability that produced the result (supplies the noun)
 * @param result anything result-shaped; see `normalizeResult`
 */
export function summarize(cap: Pick<Capability, 'name' | 'id' | 'title'>, result: unknown, opts: SummarizeOpts = {}): string {
  const now = opts.now ?? new Date();
  const r = normalizeResult(result, now);
  const view = opts.view;
  const labels = opts.labels;
  const rows = r.rows;
  const count = rows.length;

  const c = describeConstraints(r.applied, labels);
  const date = relativeDay(c.dateValue, now, c.evening);
  const nounBase = opts.noun ?? inferNoun(cap);
  const noun = pluralize(nounBase, count);
  const adjectives = c.adjectives.join(' ');

  // Grouping is a fact about the result, not the request: "across 3 cinemas"
  // is only worth saying when there is genuinely more than one group.
  let groupClause: string | undefined;
  if (view?.groupBy && count > 0) {
    const groups = groupRows(rows, view.groupBy).length;
    if (groups > 1) {
      const groupNoun = pluralize(lower(lastSegment(view.groupBy)), groups);
      groupClause = `across ${groups} ${c.city ? `${c.city} ` : ''}${groupNoun}`;
    }
  }
  // Folded into the group clause when it was used there, so the city is said once.
  const placeClause = c.place ?? (c.city && !groupClause ? `in ${c.city}` : undefined);

  const stale = staleClause(r, now);

  if (count === 0) {
    const constraint = join([adjectives, noun, c.time, date, placeClause, c.match, ...c.others]);
    const emptyHint = opts.view?.emptyText ? ` ${opts.view.emptyText}` : '';
    // "No showtimes found." reads fine bare, but "No showtimes in Zagreb found."
    // does not — once a constraint is stated, the constraint ends the sentence.
    const head = constraint ? `No ${constraint}` : `No ${noun} found`;
    return `${head}.${emptyHint}${stale}`;
  }

  const lead = join([String(count), adjectives, noun, c.time, date, groupClause, placeClause, c.match, ...c.others]);
  const best = standout(view, rows[0], labels);
  const more = r.total && r.total > count ? ` Showing the first ${count} of ${r.total}.` : '';

  return `${lead}${best ? ` — ${best}` : ''}.${more}${stale}`;
}

/** Never optional when the data is not live. */
function staleClause(r: ReturnType<typeof normalizeResult>, now: Date): string {
  const f = resolveFreshness(r.freshness, now);
  if (f.state === 'live' || f.state === 'unknown') return '';
  return ` ${f.detail}`;
}
